import type { ReactNode } from 'react';
import { Chip, cn, Dropdown, Spinner, Table } from '@heroui/react';
/*
  ⚠️ `import type`, AND IT HAS TO STAY THAT WAY. This file is rendered on the
  MEMBER home page as well as in the admin chunk, so a value import from
  react-aria-components here would pull DnD into the bundle everybody downloads.
  A type is erased at build time; `useDragAndDrop` is called in
  `pages/admin/LootRotationPage.tsx`, which is `lazy()`. See the note on
  `dragAndDropHooks` below — this is the same one-line mistake DECISION-35
  warns about for `ai-usage-charts.tsx`.
*/
import type { DragAndDropHooks } from 'react-aria-components';
import type { LootAnnouncementOutcome, LootQueueEntry } from '../data/api.ts';
import { DotsIcon } from './icons.tsx';
import { notify } from '../lib/notify.tsx';
import { describeLootAnnouncement } from '../lib/loot.ts';

/**
 * The loot rotation, as a table (DECISION-33).
 *
 * Shared by the home page and Админка → Ротация лута, because the two are the
 * same list read by two people: everyone needs to know whose turn it is, and an
 * admin additionally moves it. Splitting them into two components is how the
 * member's copy quietly stops matching the admin's — and «чья очередь» is
 * exactly the question where two answers on two screens is worse than none.
 *
 * ⚠️ THE ORDER OF `entries` IS THE DATA, and this component never sorts it.
 * `position` is stored on the server and moves only when somebody presses a
 * button; any ordering rule here would silently overrule a decision a person
 * made, which is the whole reason the derived rotation was replaced.
 */

/**
 * Pinned to 'ru-RU' rather than left to `toLocaleDateString()`'s default.
 * The bare call renders in the VIEWER's browser locale, so the same row read
 * «12.05.2026» here and «5/12/2026» on a phone set to English — inside an
 * otherwise Russian sentence, and with no way to tell which of the two numbers
 * was the month.
 */
const formatDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU');

/*
 * Sticky first columns were REMOVED (2026-08-11, owner's call).
 *
 * The idea was that a table wider than a phone should keep the row's identity
 * on screen while the rest scrolls. In practice the pinned cell has to paint an
 * opaque background of its own, and that background has to match whatever the
 * row would otherwise show — which turned out to be three different colours
 * (resting, hovered, and the viewer's own row), across two table variants, in
 * two themes. Every version of it was visibly wrong somewhere: too bright at
 * rest, or translucent on the one row whose highlight outranked it, so the
 * scrolled columns showed straight through the column that existed to stop
 * exactly that.
 *
 * The tables still scroll horizontally — `Table.ScrollContainer` — they simply
 * scroll as a whole. Do not reintroduce pinning without solving the background
 * problem first; the two bugs above are what it costs.
 */

/**
 * Keep every cell on one line.
 *
 * This is the half of the scroll story that is easy to lose: allowed to wrap,
 * the columns SQUEEZE into 390px rather than overflowing it — «Последний раз»
 * breaks over two lines, nothing ever exceeds the container, the scroll
 * container never scrolls — and a table that never scrolls is one whose columns
 * have silently been squeezed into unreadability instead.
 */
const NOWRAP_CELLS = '[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap';

/* ------------------------------------------------- the announcement notice */

/**
 * Say what came of an announcement, in the one place the app says such things.
 *
 * ⚠️ `quiet` IS `notify.note`, NOT `notify.error`, and that is the whole reason
 * `describeLootAnnouncement` returns a tone rather than a string. Five of the
 * six outcomes are the feature declining to make noise in a real group chat —
 * no loot event is close, the queue is empty, nothing moved, the bot is off, or
 * the move was a skip that is nobody's business — and an uncoloured note is
 * what keeps the sixth one legible. `send_failed` is the only one where the
 * queue moved and the group was not told, so it is the only red.
 *
 * ⚠️ THE DESCRIPTION ITSELF LIVES IN `lib/loot.ts`, and moving it there is what
 * made the six-outcomes-to-two-tones mapping testable: this file imports HeroUI,
 * and the test runner here has no DOM.
 */
export function notifyLootAnnouncement(outcome: LootAnnouncementOutcome): void {
  const notice = describeLootAnnouncement(outcome);
  if (notice.tone === 'danger') notify.error(notice.text);
  else notify.note(notice.text);
}

/** «Марина Л.» / «@marina_l2» — whichever of the two Telegram has. */
function ownerOf(entry: LootQueueEntry): { name: string | null; handle: string | null } {
  const name = [entry.memberFirstName, entry.memberLastName].filter(Boolean).join(' ');
  return {
    name: name === '' ? null : name,
    handle: entry.memberUsername === null ? null : `@${entry.memberUsername}`,
  };
}

/** One column of the queue table. Shape and reasoning as on the old page. */
interface QueueColumn {
  id: string;
  header: ReactNode;
  /** Both cells: widths and alignment, which the header and the body share. */
  className?: string;
  /** Body cells only — alignment or muting the header should not share. */
  cellClassName?: string;
  isRowHeader?: boolean;
  cell: (entry: LootQueueEntry) => ReactNode;
}

export interface LootQueueProps {
  entries: LootQueueEntry[];
  /** Whether to render the actions column at all. */
  isAdmin: boolean;
  /** The one row with a write in flight, if any. */
  busyCharacterId: number | null;
  onMoveToFront: (characterId: number) => void;
  onMoveToEnd: (characterId: number) => void;
  onCollected: (characterId: number) => void;
  onSkipped: (characterId: number) => void;
  /** The viewer's Telegram id, for marking their own row. Null when unknown. */
  ownMemberId: number | null;
  /**
   * Drag-to-reorder, when the page renders it (DECISION-43).
   *
   * ⚠️ THE HOOKS ARE BUILT BY THE CALLER, NOT HERE, AND THAT IS A BUNDLE
   * DECISION RATHER THAN A STYLE ONE. `useDragAndDrop` drags in react-aria's
   * DragManager, ListDropTargetDelegate and both collection-state hooks —
   * currently tree-shaken out of the member bundle entirely, because nothing in
   * it drags anything. Calling the hook in this file would put all of it on the
   * home page for every member, to power a control only an admin ever sees.
   * Админка → Ротация лута is `lazy()`, so calling it there charges the admin
   * chunk instead.
   *
   * Absent on Главная, deliberately: reordering the rotation is a management
   * task, and the card there already links to the page that does it.
   */
  dragAndDropHooks?: DragAndDropHooks;
  /**
   * The grip cell, supplied by whoever supplied the hooks.
   *
   * ⚠️ REACT ARIA REQUIRES A `<Button slot="drag">` INSIDE EVERY DRAGGABLE ROW —
   * it warns in development and, more importantly, without one there is no
   * keyboard or screen-reader way to start a drag at all. It is a render prop
   * for the same reason as the hooks: the button comes from
   * react-aria-components, and this file must not import it.
   */
  renderDragHandle?: (entry: LootQueueEntry) => ReactNode;
}

export function LootQueue({
  entries,
  isAdmin,
  busyCharacterId,
  onMoveToFront,
  onMoveToEnd,
  onCollected,
  onSkipped,
  ownMemberId,
  dragAndDropHooks,
  renderDragHandle,
}: LootQueueProps) {
  /*
    Both or neither. A row that is draggable with no grip cannot be dragged by
    keyboard; a grip with no hooks behind it is a button that does nothing. The
    two props exist separately only because they come from different modules,
    and reading them together here is what stops a call site shipping half of it.
  */
  const isDraggable = dragAndDropHooks !== undefined && renderDragHandle !== undefined;
  /*
    The column SET is computed, not declared. See the note that travelled with
    it: declaring six columns and hiding the last puts a six-cell header over
    five-cell rows, and every value then sits under the wrong heading.
  */
  const columns: QueueColumn[] = [
    ...(isDraggable
      ? [
          {
            id: 'handle',
            header: <span className="sr-only">Перетащить</span>,
            // `w-0` collapses it to its content: this column buys nothing for
            // the reader and spends width the five that do have to share.
            className: 'w-0 pr-0',
            cell: (entry: LootQueueEntry) => renderDragHandle!(entry),
          } satisfies QueueColumn,
        ]
      : []),
    {
      id: 'position',
      header: '#',
      className: 'w-10',
      cellClassName: 'text-muted',
      // `position`, not the array index. They agree today — the server sends
      // 1..N in order — and reading it off the array would make a future
      // partial view (a page, a filter) renumber itself from 1 while claiming
      // to be the rotation.
      cell: (entry) => entry.position,
    },
    {
      id: 'name',
      header: 'Персонаж',
      isRowHeader: true,
      /*
        The NAME is pinned, not the `#` beside it.

        Pinning column one is the usual rule and it is the wrong one here:
        column one is the rotation position, so scrolling right would leave a
        strip of bare numbers on screen while the thing they identify slid
        away. A pinned column exists to keep the row's IDENTITY readable.
      */
      cell: (entry) => <QueueIdentity entry={entry} isOwn={isOwnRow(entry, ownMemberId)} />,
    },
    {
      id: 'collected',
      header: 'Забрал',
      cellClassName: 'text-muted',
      cell: (entry) => entry.collectedCount,
    },
    {
      id: 'last',
      header: 'Последний раз',
      cellClassName: 'text-muted',
      // A dash would read as "none". Nobody having taken it yet is a fact about
      // this person, not a hole in the table.
      cell: (entry) => (entry.lastCollectedAt ? formatDate(entry.lastCollectedAt) : 'ни разу'),
    },
    {
      id: 'skipped',
      header: 'Пропустил',
      cellClassName: 'text-muted',
      // «—» at zero, unlike «Забрал», and the asymmetry is deliberate: no
      // misses is the ordinary state of almost every row, so a column of noughts
      // is noise that hides the one row that has three. `0` here would also read
      // as a score next to the counts beside it rather than as "never happened".
      cell: (entry) => (entry.skippedCount === 0 ? '—' : entry.skippedCount),
    },
    ...(isAdmin
      ? [
          {
            id: 'actions',
            header: <span className="sr-only">Действия</span>,
            className: 'text-right',
            cell: (entry: LootQueueEntry) => (
              <RowActions
                entry={entry}
                isBusy={busyCharacterId === entry.characterId}
                onMoveToFront={onMoveToFront}
                onMoveToEnd={onMoveToEnd}
                onCollected={onCollected}
                onSkipped={onSkipped}
              />
            ),
          } satisfies QueueColumn,
        ]
      : []),
  ];

  return (
    /*
      ONE table at every width, and no card-per-row phone layout.

      A rotation is read DOWN a column: the order is the whole point, and «кому
      не доставалось ни разу» is a group you spot by running your eye along one
      edge. A card list keeps each row intact and destroys exactly that — every
      count and every date ends up somewhere different on the screen. So the
      answer to a narrow screen is horizontal scroll with the name column
      pinned, not a second layout.
    */
    <Table>
      <Table.ScrollContainer>
        {/*
          `dragAndDropHooks` lives on the RAC `Table`, which is what
          `Table.Content` is — HeroUI types it as
          `Omit<ComponentPropsWithRef<typeof TablePrimitive>, 'className'>` and
          spreads the rest straight through, so this needs no HeroUI change.
          `undefined` is the same as not passing it: no drag affordance, no
          listeners, and no behaviour change for the member view.
        */}
        <Table.Content
          aria-label="Ротация лута"
          className={NOWRAP_CELLS}
          dragAndDropHooks={dragAndDropHooks}
        >
          <Table.Header>
            {columns.map((column) => (
              <Table.Column
                key={column.id}
                id={column.id}
                isRowHeader={column.isRowHeader}
                className={column.className}
              >
                {column.header}
              </Table.Column>
            ))}
          </Table.Header>
          <Table.Body>
            {entries.map((entry) => (
              <Table.Row
                key={entry.characterId}
                id={entry.characterId}
                /*
                  Your own row, marked so you can find yourself in a list
                  ordered by turn rather than by name.

                  Painted on the CELLS, not the row: HeroUI backgrounds every
                  `<td>`, so a background on the `<tr>` sits behind them and
                  shows nothing.
                */
                className={isOwnRow(entry, ownMemberId) ? '[&>td]:bg-accent-soft' : undefined}
              >
                {columns.map((column) => (
                  <Table.Cell
                    key={column.id}
                    className={cn(column.className, column.cellClassName)}
                  >
                    {column.cell(entry)}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
}

/**
 * Is this the viewer's own row?
 *
 * ⚠️ THE NULL CHECK IS NOT DEFENSIVE, IT IS THE WHOLE FUNCTION. Both sides are
 * nullable now (DECISION-33): an unowned character has `memberId === null`, and
 * `ownMemberId` is null before `/api/me` resolves. A bare `a === b` therefore
 * highlights EVERY unowned row as "вы" for a moment on every load, and
 * permanently for anyone the auth context has not named yet.
 */
function isOwnRow(entry: LootQueueEntry, ownMemberId: number | null): boolean {
  return ownMemberId !== null && entry.memberId === ownMemberId;
}

function QueueIdentity({ entry, isOwn }: { entry: LootQueueEntry; isOwn: boolean }) {
  const owner = ownerOf(entry);
  return (
    /* Capped and truncated rather than left to size itself: this column is
       pinned, so its width is spent out of the 390px the other columns scroll
       within. */
    <div className="max-w-40 min-w-0">
      <div className="flex items-baseline gap-2">
        {/* Nicknames are Latin script and stay verbatim (PRD §7). */}
        <span className="truncate font-medium">{entry.characterName}</span>
        {isOwn && (
          <Chip size="sm" variant="soft" color="accent" className="shrink-0">
            вы
          </Chip>
        )}
      </div>
      {/*
        The line under the name always says SOMETHING.

        A character with no owner is an ordinary row now — an admin created it
        for a player who is not on Telegram — and leaving its second line empty
        makes it look like a row whose owner failed to load. «без Telegram» is
        the fact, and it is muted because it is not a warning.

        An owned character may still be missing either half: Telegram guarantees
        neither a name nor a handle, so an absent one collapses rather than
        leaving a stray separator behind it.
      */}
      <div className="truncate text-xs text-muted">
        {entry.memberId === null ? (
          'без Telegram'
        ) : (
          <>
            {owner.name}
            {owner.name && owner.handle && ' · '}
            {owner.handle}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The four writes, behind one menu.
 *
 * A menu rather than four buttons in the cell: two of the four are irreversible
 * history rows, and four same-sized controls per row on a 390px screen is how
 * the wrong one gets pressed. It also keeps the actions column one glyph wide,
 * which is what lets the other five columns fit.
 *
 * ⚠️ THE ORDER OF THE ITEMS IS PLAN, PLAN, FACT, FACT, and it is not
 * alphabetical or historical. The two moves record nothing and can be undone by
 * moving somebody back; the two below them write a permanent row into the
 * history and into every counter read off it. Putting «в конец очереди»
 * anywhere but directly under «в начало очереди» would separate the pair that
 * are each other's inverse and mix the reversible half with the rest.
 */
function RowActions({
  entry,
  isBusy,
  onMoveToFront,
  onMoveToEnd,
  onCollected,
  onSkipped,
}: {
  entry: LootQueueEntry;
  isBusy: boolean;
  onMoveToFront: (characterId: number) => void;
  onMoveToEnd: (characterId: number) => void;
  onCollected: (characterId: number) => void;
  onSkipped: (characterId: number) => void;
}) {
  return (
    <Dropdown>
      {/*
        `Dropdown.Trigger` is react-aria's `Button` and renders a real
        `<button>`, but `.dropdown__trigger` carries NO button chrome — no
        padding, no background, no radius, only `display: inline-block` and the
        press animation. The hit area is ours, and it is sized to be pressable
        with a thumb rather than to the 12px glyph inside it.

        The character's name is in the `aria-label` because the visible content
        is a single dot glyph repeated on every row: a reader tabbing through
        hears the same thing N times with nothing to say whose turn is about to
        be changed, and it is not obliged to re-announce the row header on the
        way.
      */}
      <Dropdown.Trigger
        aria-label={`Действия — ${entry.characterName}`}
        isDisabled={isBusy}
        className="inline-flex size-8 items-center justify-center rounded-[var(--control-radius)] text-muted hover:bg-default"
      >
        {/* ⚠️ THE TRIGGER IS WHERE THIS ROW'S WAIT IS SHOWN. The three writes
            live behind a menu that has already closed by the time the request
            goes out, so there is no control left holding the press — without
            this the row simply greys out and the admin has nothing saying which
            of twenty rows is the one being moved. */}
        {isBusy ? (
          <span aria-hidden="true" className="flex">
            <Spinner size="sm" color="current" />
          </span>
        ) : (
          <DotsIcon className="size-4" aria-hidden="true" />
        )}
      </Dropdown.Trigger>
      {/*
        Safe inside `Table.ScrollContainer` even though that is `overflow-x:
        auto`: react-aria's `Popover` renders through `Overlay`, which portals to
        the document body, so the menu is not a descendant of the clipping box
        and cannot be cut off by it. Nothing here needs hoisting out of the cell.
      */}
      <Dropdown.Popover>
        <Dropdown.Menu>
          {/*
            Same reason as the trigger: «В начало очереди» is identical on every
            row, so the accessible name carries the name and the visible label
            stays short enough to read at a glance.

            `onAction`, not `onPress` — a menu item is react-aria's `MenuItem`,
            which fires `onAction` when it is selected by click, Enter, Space or
            typeahead. `onPress` exists on it too and only covers the pointer.
          */}
          <Dropdown.Item
            aria-label={`В начало очереди — ${entry.characterName}`}
            onAction={() => onMoveToFront(entry.characterId)}
          >
            В начало очереди
          </Dropdown.Item>
          {/*
            ⚠️ «В КОНЕЦ ОЧЕРЕДИ» IS NOT «НЕ ЯВИЛСЯ» WITH A KINDER LABEL, even
            though the two move the row to exactly the same place. That one
            writes a permanent claim that somebody's turn came up and they were
            absent, counted on every screen; this one writes nothing at all. An
            admin who simply wants somebody lower down had no way to say so
            before DECISION-43 without asserting one of the two facts below.
          */}
          <Dropdown.Item
            aria-label={`В конец очереди — ${entry.characterName}`}
            onAction={() => onMoveToEnd(entry.characterId)}
          >
            В конец очереди
          </Dropdown.Item>
          <Dropdown.Item
            aria-label={`Собрал лут — ${entry.characterName}`}
            onAction={() => onCollected(entry.characterId)}
          >
            Собрал лут
          </Dropdown.Item>
          <Dropdown.Item
            aria-label={`Не явился — ${entry.characterName}`}
            onAction={() => onSkipped(entry.characterId)}
          >
            Не явился
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}
